import React, { useContext, useEffect, useMemo, useState } from 'react';
import {
  Typography, DatePicker, TimePicker, Select, Button, Space, Table, Segmented, Checkbox, Drawer, Tag, message,
} from 'antd';
import { DownloadOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import * as XLSX from 'xlsx';
import api from '../api';
import { ThemeModeContext, UserContext } from '../App';

import { selectFilter } from '../utils/selectFilter';
// Oversight > Scheduled Staff By Hour: people Paycor has scheduled in each half hour
// (or hour) of each day, for the shift departments picked -- the pivot that
// used to be built by hand in a Google sheet. See routes/scheduled_staffing.py
// for how a person is counted. Future schedules reach as far as the import
// pulls them (today + 14 days). The departments offered are the ones mapped to
// the viewer's Visible Teams on Admin > Teams (all of them for admin, and for
// qa_admin with no Visible Teams), checked again by the API.

const { RangePicker } = DatePicker;
// Whole numbers as they are; a deducted share to 2 places, no trailing zeros.
const fmtNum = (v) => (Number.isInteger(v) ? v : Number(v.toFixed(2)));
const slotLabel = (m) => dayjs().startOf('day').add(m, 'minute').format('h:mm A');

export default function ScheduledStaffingPage() {
  const [range, setRange]         = useState([dayjs().startOf('day'), dayjs().add(13, 'day').startOf('day')]);
  const [departments, setDepts]   = useState([]);
  const [deptOptions, setOptions] = useState([]);
  const [interval, setSlotSize]  = useState('30');
  const [hideEmpty, setHideEmpty] = useState(true);
  // Time window shown (display only, no re-run): From / To as dayjs times.
  // From after To is an overnight window: each date's column runs from From
  // that evening into the next morning, up to To.
  const [winFrom, setWinFrom]     = useState(null);
  const [winTo, setWinTo]         = useState(null);
  const [data, setData]           = useState(null);
  const [loading, setLoading]     = useState(false);
  // Deduct absence notifications: Absences access only (the API checks too).
  const currentUser = useContext(UserContext);
  const canAbsences = currentUser?.Role !== 'agent' && (currentUser?.CanEditAbsences ?? 0) >= 1;
  const [deduct, setDeduct]       = useState(false);
  // The people behind a clicked cell: { title, rows } (rows null = loading).
  const [people, setPeople]       = useState(null);

  const params = () => ({ start: range[0].format('YYYY-MM-DD'), end: range[1].format('YYYY-MM-DD') });

  // The departments with shifts in the chosen dates.
  useEffect(() => {
    if (!range?.[0] || !range?.[1]) return;
    api.get('/api/scheduled-staffing/departments', { params: params() })
      .then((r) => setOptions((r.data || []).map((n) => ({ value: n, label: n }))))
      .catch(() => setOptions([]));
  }, [range]); // eslint-disable-line react-hooks/exhaustive-deps

  const run = (deductAbsences = deduct) => {
    if (!departments.length) { message.warning('Pick a department.'); return; }
    setLoading(true);
    const qs = new URLSearchParams({ ...params(), interval, ...(deductAbsences && canAbsences ? { deduct_absences: '1' } : {}) });
    departments.forEach((d) => qs.append('department', d));
    api.get(`/api/scheduled-staffing?${qs.toString()}`)
      .then((r) => setData({ ...r.data, departments: [...departments], interval }))
      .catch((err) => message.error(err.response?.data?.error || 'Failed to load the schedule.'))
      .finally(() => setLoading(false));
  };

  // Rows: one per slot; with Hide empty, only from the first to the last slot
  // anyone is scheduled in, across all the days. With absences deducted the
  // cell value (r[date]) is the people left; r[`${date}|s`] the scheduled and
  // r[`${date}|a`] the absent.
  const { rows, max } = useMemo(() => {
    if (!data) return { rows: [], max: 0 };
    const fromM = winFrom ? winFrom.hour() * 60 + winFrom.minute() : null;
    const toM = winTo ? winTo.hour() * 60 + winTo.minute() : null;
    const overnight = fromM != null && toM != null && fromM > toM;
    const inWindow = (m) => {
      if (fromM == null && toM == null) return true;
      if (fromM == null) return m < toM;
      if (toM == null) return m >= fromM;
      if (fromM === toM) return true;
      return overnight ? (m >= fromM || m < toM) : (m >= fromM && m < toM);
    };
    const nextDay = (d) => dayjs(d).add(1, 'day').format('YYYY-MM-DD');
    let slotIdx = data.slots.map((m, i) => i).filter((i) => inWindow(data.slots[i]));
    // Overnight: the evening first, then the next morning.
    if (overnight) slotIdx = [...slotIdx.filter((i) => data.slots[i] >= fromM), ...slotIdx.filter((i) => data.slots[i] < toM)];
    const all = slotIdx.map((i) => {
      const m = data.slots[i];
      return {
        key: m, Slot: slotLabel(m),
        ...Object.fromEntries(data.dates.flatMap((d) => {
          // The morning part of an overnight window is the next day's.
          const src = overnight && m < toM ? nextDay(d) : d;
          if (!data.counts[src]) return [[d, null], [`${d}|s`, 0], [`${d}|a`, 0], [`${d}|day`, src]];
          const sched = data.counts[src][i];
          const away = data.absent ? data.absent[src][i] : 0;
          // Absences deduct the share of the slot they cover, so these can be decimals.
          return [[d, Math.round((sched - away) * 100) / 100], [`${d}|s`, sched], [`${d}|a`, away], [`${d}|day`, src]];
        })),
      };
    });
    let first = 0;
    let last = all.length - 1;
    if (hideEmpty) {
      const busy = (r) => data.dates.some((d) => r[`${d}|s`] > 0);
      while (first <= last && !busy(all[first])) first += 1;
      while (last >= first && !busy(all[last])) last -= 1;
    }
    const shown = first <= last ? all.slice(first, last + 1) : [];
    const peak = Math.max(0, ...shown.flatMap((r) => data.dates.map((d) => r[d] || 0)));
    return { rows: shown, max: peak };
  }, [data, hideEmpty, winFrom, winTo]);

  // Shading by how busy the slot is against the busiest one shown: one blue,
  // light to strong, on the whole cell. Stronger in dark mode, where a faint
  // tint would vanish into the background (orange there read as brown).
  const { dark } = useContext(ThemeModeContext);
  const shade = (v) => {
    if (!v || !max) return {};
    const t = v / max;
    const a = dark ? 0.18 + 0.62 * t : 0.08 + 0.52 * t;
    return {
      background: `rgba(${dark ? '64, 150, 255' : '22, 119, 255'}, ${a.toFixed(2)})`,
      ...(dark && t > 0.55 ? { color: '#fff', fontWeight: 600 } : {}),
    };
  };
  const cell = (v) => (v == null ? <span style={{ color: '#bfbfbf' }}>—</span>
    : v ? fmtNum(v) : <span style={{ color: '#bfbfbf' }}>0</span>);
  // Deducting: what is left on top, and under it how it was reached --
  // (scheduled - absent), e.g. 2 over (3-1). The shading follows what is left.
  const cellFor = (d) => (v, r) => {
    const away = r[`${d}|a`];
    if (!away) return cell(v);
    return (
      <div style={{ lineHeight: 1.15, whiteSpace: 'nowrap' }}>
        <div><strong>{fmtNum(v)}</strong></div>
        {/* All red, so a cell with an absence catches the eye. */}
        <div style={{ fontSize: 11, color: '#ff4d4f', fontWeight: 600 }}>
          ({fmtNum(r[`${d}|s`])}-{fmtNum(away)})
        </div>
      </div>
    );
  };

  // Who is scheduled in a slot, for the departments the grid was run for.
  const openPeople = (day, row) => {
    const title = `${dayjs(day).format('ddd M/D/YY')} ${row.Slot}`;
    setPeople({ title, rows: null });
    const qs = new URLSearchParams({ date: day, slot: String(row.key), interval: String(data.interval || interval) });
    (data.departments || departments).forEach((d) => qs.append('department', d));
    api.get(`/api/scheduled-staffing/people?${qs.toString()}`)
      .then((r) => setPeople((p) => (p && p.title === title ? { title, rows: r.data || [] } : p)))
      .catch((err) => { message.error(err.response?.data?.error || 'Failed to load the people.'); setPeople(null); });
  };

  const columns = data ? [
    { title: interval === '60' ? 'Hour' : 'Half Hour', dataIndex: 'Slot', key: 'Slot', fixed: 'left', width: 95 },
    ...data.dates.map((d) => {
      const day = dayjs(d);
      const weekend = day.day() === 0 || day.day() === 6;
      return {
        key: d, dataIndex: d, width: 78, align: 'center',
        title: (
          <div style={{ lineHeight: 1.2, whiteSpace: 'nowrap' }}>
            <div>{day.format('M/D/YY')}</div>
            <div style={{ fontWeight: 400, fontSize: 12, color: weekend ? '#e8682a' : '#8c8c8c' }}>{day.format('dddd')}</div>
          </div>
        ),
        render: cellFor(d),
        onCell: (r) => ({
          style: { ...shade(r[d]), ...(r[`${d}|s`] ? { cursor: 'pointer' } : {}) },
          onClick: r[`${d}|s`] ? () => openPeople(r[`${d}|day`] || d, r) : undefined,
        }),
      };
    }),
  ] : [];

  const exportXlsx = () => {
    const header = ['Half hour', ...data.dates.map((d) => dayjs(d).format('M/D/YY'))];
    const days = ['', ...data.dates.map((d) => dayjs(d).format('dddd'))];
    const body = rows.map((r) => [r.Slot, ...data.dates.map((d) => r[d])]);
    const foot = [
      ['People scheduled', ...data.dates.map((d) => data.days[d].Headcount)],
      // Deducting, the cells above are the people left after absences.
      ...(data.absent ? [['Absent', ...data.dates.map((d) => data.days[d].Absent)]] : []),
      ['Scheduled hours', ...data.dates.map((d) => data.days[d].Hours)],
      ...(data.absent ? [
        ['Hours after absences', ...data.dates.map((d) => Math.round((data.days[d].Hours - data.days[d].AbsentHours) * 100) / 100)],
      ] : []),
    ];
    header[0] = interval === '60' ? 'Hour' : 'Half hour';
    const ws = XLSX.utils.aoa_to_sheet([[departments.join(', ')], header, days, ...body, [], ...foot]);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Scheduled Staff By Hour');
    XLSX.writeFile(wb, `scheduled-staff-by-hour-${data.dates[0]}-to-${data.dates[data.dates.length - 1]}.xlsx`);
  };

  return (
    <div>
      <Typography.Title level={4} style={{ marginTop: 0 }}>Scheduled Staff By Hour</Typography.Title>

      <Space wrap style={{ marginBottom: 12 }}>
        <span>Dates:</span>
        <RangePicker value={range} onChange={(v) => v && setRange(v)} allowClear={false} />
        <span>Department:</span>
        <Select mode="multiple" allowClear showSearch filterOption={selectFilter} placeholder="Pick a department" style={{ minWidth: 260 }}
          maxTagCount="responsive" value={departments} onChange={setDepts} options={deptOptions} />
        <Segmented value={interval} onChange={setSlotSize}
          options={[{ label: 'Half Hour', value: '30' }, { label: 'Hour', value: '60' }]} />
        <span>From:</span>
        <TimePicker value={winFrom} onChange={setWinFrom} use12Hours format="h:mm A"
          minuteStep={30} showNow={false} needConfirm={false} placeholder="12:00 AM" style={{ width: 120 }} />
        <span>To:</span>
        <TimePicker value={winTo} onChange={setWinTo} use12Hours format="h:mm A"
          minuteStep={30} showNow={false} needConfirm={false} placeholder="11:59 PM" style={{ width: 120 }} />
        <Checkbox checked={hideEmpty} onChange={(e) => setHideEmpty(e.target.checked)}>Hide empty hours</Checkbox>
        {canAbsences && (
          <Checkbox checked={deduct}
            // Takes effect on the next Run: deducting is the heavier query.
            onChange={(e) => setDeduct(e.target.checked)}>
            Deduct absence notifications
          </Checkbox>
        )}
        <Button type="primary" onClick={() => run()} loading={loading}>Run</Button>
        {data && rows.length > 0 && <Button icon={<DownloadOutlined />} onClick={exportXlsx}>Excel</Button>}
      </Space>

      {data && (
        <Table
          rowKey="key"
          size="small"
          bordered
          loading={loading}
          dataSource={rows}
          columns={columns}
          pagination={false}
          scroll={{ x: 'max-content' }}
          // Dates stay in view while scrolling down the half hours.
          sticky={{ offsetHeader: 0 }}
          locale={{ emptyText: 'Nobody is scheduled in these departments on these dates.' }}
          summary={() => (rows.length ? (
            <Table.Summary fixed>
              <Table.Summary.Row>
                <Table.Summary.Cell index={0}><strong>People</strong></Table.Summary.Cell>
                {data.dates.map((d, i) => (
                  <Table.Summary.Cell key={d} index={i + 1} align="center"><strong>{data.days[d].Headcount}</strong></Table.Summary.Cell>
                ))}
              </Table.Summary.Row>
              {data.absent && (
                <Table.Summary.Row>
                  <Table.Summary.Cell index={0}><strong style={{ color: '#ff4d4f' }}>Absent</strong></Table.Summary.Cell>
                  {data.dates.map((d, i) => (
                    <Table.Summary.Cell key={d} index={i + 1} align="center">
                      {data.days[d].Absent ? <span style={{ color: '#ff4d4f', fontWeight: 600 }}>-{data.days[d].Absent}</span> : 0}
                    </Table.Summary.Cell>
                  ))}
                </Table.Summary.Row>
              )}
              <Table.Summary.Row>
                <Table.Summary.Cell index={0}><strong>Hours</strong></Table.Summary.Cell>
                {data.dates.map((d, i) => (
                  <Table.Summary.Cell key={d} index={i + 1} align="center">{data.days[d].Hours}</Table.Summary.Cell>
                ))}
              </Table.Summary.Row>
              {data.absent && (<>
                <Table.Summary.Row>
                  <Table.Summary.Cell index={0}><strong>Hours after absences</strong></Table.Summary.Cell>
                  {data.dates.map((d, i) => (
                    <Table.Summary.Cell key={d} index={i + 1} align="center">
                      <strong>{Math.round((data.days[d].Hours - data.days[d].AbsentHours) * 100) / 100}</strong>
                    </Table.Summary.Cell>
                  ))}
                </Table.Summary.Row>
              </>)}
            </Table.Summary>
          ) : null)}
        />
      )}
      <Drawer open={!!people} onClose={() => setPeople(null)} width={640} destroyOnClose
        title={people ? `Scheduled: ${people.title}` : ''}>
        <Table rowKey="EmployeeNumber" size="small" pagination={false} loading={people?.rows === null}
          dataSource={people?.rows || []}
          locale={{ emptyText: 'Nobody.' }}
          columns={[
            { title: 'Name', dataIndex: 'Name', key: 'Name', sorter: (a, b) => a.Name.localeCompare(b.Name) },
            { title: 'Shift', dataIndex: 'Shift', key: 'Shift', width: 190 },
            { title: 'Department', dataIndex: 'Department', key: 'Department', width: 150 },
            { title: 'Shift Name', dataIndex: 'ShiftName', key: 'ShiftName', width: 130, render: (v) => v || '—' },
            ...(canAbsences ? [{ title: 'Absence', dataIndex: 'Absences', key: 'Absences', width: 190,
              render: (v) => ((v || []).length
                ? v.map((a, i) => <Tag key={i} color="red" style={{ marginBottom: 2 }}>{a.Reason} {a.From} - {a.To}</Tag>)
                : '') }] : []),
          ]} />
      </Drawer>
    </div>
  );
}
